/*
 * OPTIMIZED REGISTRATION FORM - REDUX STATE WATCHER APPROACH
 * 
 * FIXED ISSUES:
 * ✅ Removed duplicate state declarations
 * ✅ Consistent useEffect-based Redux state watching
 * ✅ Proper parameter passing to handleCognitoSignUp
 * ✅ Fixed navigation with stored email
 * ✅ Clean separation between Cognito signup and MongoDB user creation
 * ✅ Fixed "Cannot access before initialization" error by moving hooks up
 * 
 * Clean Solution for Redux-based Cognito Hook:
 * 
 * 1. User clicks submit → handleRegistration() called
 * 2. Store form data → setFormDataForSubmission()
 * 3. Call handleCognitoSignUp() → dispatches Redux action (returns boolean)
 * 4. Set waitingForCognito(true) → starts watching process
 * 5. useEffect watches cognito.userSub in Redux state
 * 6. When userSub appears → automatically trigger createUserDb()
 * 7. Complete registration and navigate
 * 
 * Benefits:
 * ✅ No polling/timeout loops
 * ✅ Reactive to Redux state changes
 * ✅ Clean separation of concerns
 * ✅ Automatic error handling and timeout
 * ✅ Better user feedback with loading states
 * ✅ Proper hook ordering to prevent initialization errors
 * 
 * Flow Control:
 * - waitingForCognito: Indicates we're waiting for Redux userSub
 * - formDataForSubmission: Stores form data for useEffect to use
 * - Timeout protection (15 seconds) in case Redux never updates
 * 
 * NOTE: If your CreateCognitoPayload interface requires password,
 * add it to the handleCognitoSignUp parameters:
 * 
 * const cognitoSuccess = await handleCognitoSignUp({
 *   given_name: formData.given_name,
 *   family_name: formData.family_name, 
 *   practice_id: '',
 *   email: formData.email,
 *   role: 'practice_admin',
 *   password: formData.password,  // Add this line
 * });
 * 
 * And update your interface to include password:
 * interface CreateCognitoPayload {
 *   given_name: string;
 *   family_name: string;
 *   practice_id: string;
 *   email: string;
 *   role: string;
 *   password: string;  // Add this
 * }
 */

import React, { useCallback, useMemo, useState, memo, useEffect } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { useForm, FieldError } from "react-hook-form";
import { useNavigate } from "react-router-dom";
// REPLACE WITH YOUR ACTUAL IMPORTS:
import useCognitoSignUp from "../../hooks/Auth/useCognitoSignUp";
import useCreateUser from "../../hooks/User/useCreateUser";
import useCreatePractice from "../../hooks/Practice/useCreatePractice";

// TypeScript interfaces
interface FormData {
  email: string;
  password: string;
  confirmPassword: string;
  given_name: string;
  status: string;
  family_name: string;
  phone_number: string;
  practice_name: string;
  practice_dba: string;
  practice_ein: string;
  practice_phone: string;
  practice_website: string;
  practice_npi_type: string;
  practice_npi_number: string;
  termsAccepted: boolean;
}



interface FormInputProps {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  type?: string;
  error?: FieldError | undefined;
  success?: boolean;
  register?: ReturnType<ReturnType<typeof useForm<FormData>>['register']>;
  placeholder?: string;
  maxLength?: number;
  className?: string;
  isPhone?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  [key: string]: unknown;
}

interface PasswordInputProps {
  label: string;
  register?: ReturnType<ReturnType<typeof useForm<FormData>>['register']>;
  error?: FieldError | undefined;
  showPassword: boolean;
  onToggleShow: () => void;
  placeholder?: string;
}

interface PasswordStrengthProps {
  password: string;
}

interface ProgressIndicatorProps {
  currentStep: string;
  steps: Array<{
    key: string;
    title: string;
    icon: React.ComponentType<{ className?: string }>;
    description: string;
  }>;
}


// Optimized icon imports
import {
  User, Building2, CheckCircle, ArrowRight, ArrowLeft, Mail, Phone,
  Globe, FileText, AlertCircle, Check, Lock, Eye, EyeOff, Loader2
} from 'lucide-react';

// Utility functions
const formatPhoneNumber = (value: string) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const validateEmail = (email: string) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email);

const validatePassword = (password: string) => {
  const checks = {
    minLength: password.length >= 8,
    hasUpperCase: /[A-Z]/.test(password),
    hasLowerCase: /[a-z]/.test(password),
    hasNumbers: /\d/.test(password),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
  };

  return {
    ...checks,
    isValid: Object.values(checks).every(Boolean)
  };
};

// Optimized FormInput Component
const FormInput = memo<FormInputProps>(({
  label, icon: Icon, type = "text", error, success, register,
  placeholder, maxLength, className = "", isPhone = false, onChange, ...props
}) => {
  const handleInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (isPhone) {
      const formatted = formatPhoneNumber(e.target.value);
      e.target.value = formatted;
    }
    onChange?.(e);
  }, [isPhone, onChange]);

  return (
    <div className={className}>
      <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
        {Icon && <Icon className="w-4 h-4 mr-2 text-indigo-600" />}
        {label}
      </label>
      <div className="relative">
        <input
          type={type}
          {...(register || {})}
          maxLength={maxLength}
          onInput={handleInput}
          className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-200 ${error
              ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
              : success
                ? "border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20"
                : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 hover:border-gray-300"
            } placeholder-gray-400 focus:outline-none focus:ring-4`}
          placeholder={placeholder}
          {...props}
        />
        {success && (
          <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-emerald-500" />
        )}
      </div>
      {error && (
        <p className="mt-1 text-sm text-red-600 flex items-center">
          <AlertCircle className="w-4 h-4 mr-1" />
          {error.message}
        </p>
      )}
      {isPhone && (
        <p className="mt-1 text-xs text-gray-500">Format: (555) 123-4567</p>
      )}
    </div>
  );
});

const PasswordInput = memo<PasswordInputProps>(({ label, register, error, showPassword, onToggleShow, placeholder }) => (
  <div>
    <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
      <Lock className="w-4 h-4 mr-2 text-indigo-600" />
      {label}
    </label>
    <div className="relative">
      <input
        type={showPassword ? "text" : "password"}
        {...(register || {})}
        className={`w-full px-4 py-3 pr-12 rounded-xl border-2 transition-all duration-200 ${error
            ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
            : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 hover:border-gray-300"
          } placeholder-gray-400 focus:outline-none focus:ring-4`}
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={onToggleShow}
        tabIndex={-1}
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
      >
        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
      </button>
    </div>
    {error && (
      <p className="mt-1 text-sm text-red-600 flex items-center">
        <AlertCircle className="w-4 h-4 mr-1" />
        {error.message}
      </p>
    )}
  </div>
));

// Optimized Password Strength Component
const PasswordStrength = memo<PasswordStrengthProps>(({ password }) => {
  const passwordStrength = useMemo(() =>
    password ? validatePassword(password) : null, [password]
  );

  const requirements = useMemo(() => {
    if (!passwordStrength) return [];
    return [
      { key: 'minLength', label: 'At least 8 characters', met: passwordStrength.minLength },
      { key: 'hasUpperCase', label: 'One uppercase letter', met: passwordStrength.hasUpperCase },
      { key: 'hasLowerCase', label: 'One lowercase letter', met: passwordStrength.hasLowerCase },
      { key: 'hasNumbers', label: 'One number', met: passwordStrength.hasNumbers },
      { key: 'hasSpecialChar', label: 'One special character', met: passwordStrength.hasSpecialChar },
    ];
  }, [passwordStrength]);

  if (!password || !passwordStrength) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      className="mt-2 p-3 bg-gray-50 rounded-lg border"
    >
      <p className="text-xs font-medium text-gray-700 mb-2">Password Requirements:</p>
      <div className="space-y-1">
        {requirements.map((req) => (
          <div key={req.key} className="flex items-center text-xs">
            {req.met ? (
              <Check className="w-3 h-3 text-green-500 mr-2" />
            ) : (
              <div className="w-3 h-3 rounded-full bg-gray-300 mr-2" />
            )}
            <span className={req.met ? 'text-green-700' : 'text-gray-600'}>
              {req.label}
            </span>
          </div>
        ))}
      </div>
    </motion.div>
  );
});

// Optimized Progress Indicator
const ProgressIndicator = memo<ProgressIndicatorProps>(({ currentStep, steps }) => {
  const currentStepIndex = useMemo(() =>
    steps.findIndex((step) => step.key === currentStep), [currentStep, steps]
  );

  return (
    <div className="mb-8">
      <div className="flex items-center justify-center">
        {steps.map((step, index: number) => {
          const StepIcon = step.icon;
          const isActive = index === currentStepIndex;
          const isCompleted = index < currentStepIndex;

          return (
            <div key={step.key} className="flex items-center">
              <motion.div
                animate={{ scale: isActive ? 1.05 : 1 }}
                transition={{ duration: 0.3 }}
                className={`relative flex items-center justify-center w-8 h-8 rounded-full shadow-lg border-2 ${isCompleted
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : isActive
                      ? 'bg-indigo-600 border-indigo-600 text-white'
                      : 'bg-white border-gray-300 text-gray-400'
                  }`}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5 text-white" />
                ) : (
                  <StepIcon className="w-5 h-5" />
                )}

                {isActive && (
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1.3, opacity: 0.3 }}
                    className="absolute inset-0 bg-indigo-600 rounded-full animate-pulse"
                  />
                )}
              </motion.div>

              <div className="ml-3 mr-6">
                <p className={`text-sm font-semibold ${isActive ? 'text-indigo-600' : isCompleted ? 'text-emerald-600' : 'text-gray-500'
                  }`}>
                  {step.title}
                </p>
                <p className="text-xs text-gray-500">{step.description}</p>
              </div>

              {index < steps.length - 1 && (
                <motion.div
                  animate={{
                    backgroundColor: index < currentStepIndex ? '#10b981' : '#e5e7eb'
                  }}
                  transition={{ duration: 0.3 }}
                  className="h-1 w-16 rounded-full mr-6"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Main Registration Component - New Four-Step Flow
const RegistrationPage = () => {
  const navigate = useNavigate();

  // Consolidated state
  const [currentStep, setCurrentStep] = useState('account');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [registrationStep, setRegistrationStep] = useState<'idle' | 'creating-practice' | 'creating-cognito' | 'creating-user' | 'complete'>('idle');
  const [createdPracticeId, setCreatedPracticeId] = useState<string | null>(null);
  const [formDataForSubmission, setFormDataForSubmission] = useState<FormData | null>(null);

  // API hooks
  const { handleCreateUser: handleCognitoSignUp, cognito } = useCognitoSignUp();
  const { createUserDb } = useCreateUser();
  const { create: createPractice, practice: createdPractice } = useCreatePractice();

  // Step 1: Watch for practice creation success
  useEffect(() => {
    if (createdPractice && registrationStep === 'creating-practice' && formDataForSubmission) {


      // Extract practice ID from the created practice
      const practiceId = createdPractice.id || createdPractice._id;

      if (practiceId) {
        setCreatedPracticeId(practiceId);
        setRegistrationStep('creating-cognito');
      } else {
        console.error('❌ Practice created but no ID found');
        setSubmitError('Practice created but ID not found. Please try again.');
        setIsSubmitting(false);
        setRegistrationStep('idle');
      }
    }
  }, [createdPractice, registrationStep, formDataForSubmission]);

  // Step 2: Create Cognito user with practice ID
  useEffect(() => {
    const createCognitoUserWithPracticeId = async () => {
      if (registrationStep !== 'creating-cognito' || !createdPracticeId || !formDataForSubmission) {
        return;
      }

      // Ensure all required fields are present
      const { given_name, family_name, email, password } = formDataForSubmission;
      if (!given_name || !family_name || !createdPracticeId || !email || !password) {
        setSubmitError('All account fields are required for registration.');
        setIsSubmitting(false);
        setRegistrationStep('idle');
        return;
      }


      try {
        // All required fields for Cognito sign-up are included below
        const cognitoPayload = {
          given_name: formDataForSubmission.given_name,
          family_name: formDataForSubmission.family_name,
          practice_id: createdPracticeId, // This will be stored as custom:id_practice
          email: formDataForSubmission.email,
          role: 'practice_admin',
          password: formDataForSubmission.password,
        };
        const cognitoSuccess = await handleCognitoSignUp(cognitoPayload);

        if (!cognitoSuccess) {
          throw new Error('Failed to create Cognito user');
        }

        setRegistrationStep('creating-user');

      } catch (error: unknown) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
        console.error('💥 Cognito user creation failed:', error);
        setSubmitError(`Failed to create Cognito user: ${errorMessage}`);
        setIsSubmitting(false);
        setRegistrationStep('idle');
      }
    };

    createCognitoUserWithPracticeId();
  }, [registrationStep, createdPracticeId, formDataForSubmission, handleCognitoSignUp]);

  // Step 3: Watch for cognito.userSub and create database user
  useEffect(() => {
    const createDatabaseUser = async () => {
      if (registrationStep !== 'creating-user' || !cognito?.userSub || !formDataForSubmission || !createdPracticeId) {
        return;
      }


      try {
        const userData = {
          email: formDataForSubmission.email,
          firstName: formDataForSubmission.given_name,
          lastName: formDataForSubmission.family_name,
          phone_number: formDataForSubmission.phone_number,
          cognito_user_id: cognito.userSub,
          role: 'practice_admin',
          practice_id: createdPracticeId, // Link to the created practice
          stripe_customer_id: '',
          practice: {
            name: formDataForSubmission.practice_name,
            dba: formDataForSubmission.practice_dba,
            ein: formDataForSubmission.practice_ein,
            primary_email: formDataForSubmission.email,
            billing_email: formDataForSubmission.email,
            phone: formDataForSubmission.phone_number,
            website: formDataForSubmission.practice_website,
            npi_type: formDataForSubmission.practice_npi_type,
            npi_number: formDataForSubmission.practice_npi_number
          }
        };

        const dbUser = await createUserDb(userData);
        const userEmail = formDataForSubmission.email;

        setRegistrationComplete(true);
        setRegistrationStep('complete');
        setFormDataForSubmission(null);

        // Navigate to confirmation page
        setTimeout(() => {
          navigate('/confirm-email', {
            state: {
              registrationComplete: true,
              userId: (dbUser as { id?: string; data?: { mongoUserId?: string } })?.id || (dbUser as { data?: { mongoUserId?: string } })?.data?.mongoUserId,
              practiceId: createdPracticeId,
              email: userEmail,
              userSub: cognito.userSub,
              role: 'practice_admin'
            }
          });
        }, 2000);

      } catch (error: unknown) {
        console.error('💥 Database user creation failed:', error);

        let errorMessage = 'Failed to create user account. Please try again.';
        if (error && typeof error === 'object' && 'message' in error) {
          errorMessage = String(error.message);
        } else if (error && typeof error === 'object' && 'response' in error) {
          const response = (error as { response?: { data?: { message?: string; error?: string } } }).response;
          if (response?.data?.message) {
            errorMessage = response.data.message;
          } else if (response?.data?.error) {
            errorMessage = response.data.error;
          }
        }

        setSubmitError(errorMessage);
        setRegistrationStep('idle');
      } finally {
        setIsSubmitting(false);
      }
    };

    createDatabaseUser();
  }, [registrationStep, cognito, formDataForSubmission, createdPracticeId, createUserDb, navigate]);

  // Unified form with all fields
  const form = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      // Account fields
      email: '',
      password: '',
      confirmPassword: '',
      given_name: '',
      family_name: '',
      phone_number: '',
      status: 'active',
      // Practice fields
      practice_name: '',
      practice_dba: '',
      practice_ein: '',
      practice_phone: '',
      practice_website: '',
      practice_npi_type: 'NA',
      practice_npi_number: '',
      termsAccepted: false
    }
  });

  const { register, formState: { errors }, watch, setValue, trigger } = form;
  const formData = watch();
  const confirmPassword = watch("confirmPassword");

  // Step configuration
  const stepConfig = useMemo(() => [
    { key: 'account', title: 'Account', icon: User, description: '' },
    { key: 'practice', title: 'Basic Data', icon: Building2, description: '' },
    { key: 'confirmation', title: 'Security', icon: CheckCircle, description: '' }
  ], []);

  // Validation for each step
  const stepValidation = useMemo(() => ({
    account: formData.password && formData.confirmPassword &&
      formData.given_name && formData.family_name && formData.phone_number &&
      !errors.password && !errors.confirmPassword &&
      !errors.given_name && !errors.family_name && !errors.phone_number,
    practice: formData.practice_name && formData.practice_ein &&
      !errors.practice_name && !errors.practice_ein &&
      !errors.phone_number && formData.practice_npi_type && !errors.practice_npi_type
  }), [formData, errors]);

  // Optimized step transition
  const goToStep = useCallback(async (step: string) => {
    // Validate current step before proceeding
    if (currentStep === 'account') {
      const accountFieldsValid = await trigger([
        'email', 'password', 'confirmPassword', 'given_name', 'family_name', 'phone_number'
      ]);

      if (!accountFieldsValid) return;

      // const current_email = watch("email");

      // Verify email availability before proceeding
      // if (current_email) {
      //   const isEmailAvailable = await verifyEmailAvailability(current_email);
        
      //   if (!isEmailAvailable) {
      //     // Show modal explaining to user that email is taken

      //     // Show register error for email field

      //     return;
      //   }
      // }
    } else if (currentStep === 'practice') {
      const practiceFieldsValid = await trigger([
        'practice_name', 'practice_ein', 'practice_phone'
      ]);
      if (!practiceFieldsValid) return;
    }

    setCurrentStep(step);
  }, [currentStep, trigger]);

  // Optimized phone number handler
  const handlePhoneChange = useCallback((field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedPhone = formatPhoneNumber(e.target.value);
    setValue(field as keyof FormData, formattedPhone);
  }, [setValue]);

  // NPI type change handler
  const handleNpiTypeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const npiType = e.target.value;
    setValue('practice_npi_type', npiType);
    if (!npiType) {
      setValue('practice_npi_number', '');
    }
  }, [setValue]);

  // NEW: Four-step registration handler
  const handleRegistration = useCallback(async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);
    setRegistrationComplete(false);
    setRegistrationStep('creating-practice');

    try {

      // Store form data for use in subsequent steps
      setFormDataForSubmission(formData);

      // Step 1: Create Practice
      const practiceData = {
        id: '', // This will be generated by the backend
        name: formData.practice_name,
        DBA: formData.practice_dba || '',
        ein: formData.practice_ein,
        primary_email: formData.email,
        billing_email: formData.email,
        // phone: formData.practice_phone,
        phone: formData.phone_number,
        website: formData.practice_website || '',
        npi_type: formData.practice_npi_type || '',
        npi_number: formData.practice_npi_number || '',
        locations: [],
        clinician_ids: [],
        accepted_insurance: [],
        social_media: {},
        google_ads_daily_budget: 10,
      };



      createPractice(practiceData);

      // The rest of the flow will be handled by useEffect hooks:
      // 1. Practice creation success -> Step 2: Cognito signup with practice ID
      // 2. Cognito success -> Step 3: Database user creation
      // 3. User creation success -> Step 4: EHR integration (handled separately)

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('💥 Registration initiation failed:', error);
      setSubmitError(`Registration failed: ${errorMessage}`);
      setIsSubmitting(false);
      setRegistrationStep('idle');
    }
  }, [formData, isSubmitting, createPractice]);

  // Submit button content with step-specific messaging
  const submitButtonContent = useMemo(() => {
    if (registrationComplete) {
      return (
        <>
          <CheckCircle className="w-5 h-5 mr-2" />
          Registration Complete!
        </>
      );
    }

    if (isSubmitting) {
      switch (registrationStep) {
        case 'creating-practice':
          return (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Creating Practice...
            </>
          );
        case 'creating-cognito':
          return (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Setting Up Account...
            </>
          );
        case 'creating-user':
          return (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Finalizing Registration...
            </>
          );
        default:
          return (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </>
          );
      }
    }

    return (
      <>
        <CheckCircle className="w-5 h-5 mr-2" />
        Complete Registration
      </>
    );
  }, [isSubmitting, registrationComplete, registrationStep]);

  // Add this after the other hook declarations
  const verifyEmailAvailability = async (email: string) => {
    let rslt = false;

    try {
      // Make API call to verify email
      const response = await fetch('https://ycx2wdyct1.execute-api.us-east-2.amazonaws.com/dev/auth/exists', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      if (!response.ok) {
        throw new Error('Could not verify email availability');
      }

      // Get response data
      let data = await response.json();

      // Check if body exists
      if (data.body && data.body.userExists !== undefined && typeof data.body.userExists === 'boolean') {
        rslt = !data.body.userExists;
      }

    } catch (error) {
      if (error instanceof Error) {
        setSubmitError(error.message);
      } else {
        setSubmitError('Failed to verify email availability');
      }
    } finally {
      return rslt;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-4xl"
      >
        <div className="bg-white p-8 md:p-10 shadow-2xl rounded-2xl border border-gray-100">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Create Your Account
            </h2>
            <p className="text-gray-600">
              Step {stepConfig.findIndex(step => step.key === currentStep) + 1} of {stepConfig.length} - {stepConfig.find(step => step.key === currentStep)?.description}
            </p>
          </div>

          <ProgressIndicator currentStep={currentStep} steps={stepConfig} />

          <div className="min-h-[600px]">
            {/* Account Step */}
            {currentStep === 'account' && (
              <motion.div
                key="account"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="First Name *"
                    icon={User}
                    register={register("given_name", { required: "First name is required" })}
                    error={errors.given_name}
                    placeholder="John"
                  />

                  <FormInput
                    label="Last Name *"
                    register={register("family_name", { required: "Last name is required" })}
                    error={errors.family_name}
                    placeholder="Doe"
                  />

                  <FormInput
                    label="Email *"
                    icon={Mail}
                    className="md:col-span-2"
                    type="email"
                    register={register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Please enter a valid email address"
                      },
                      validate: {
                        availability: async (value) => {
                          const isAvailable = await verifyEmailAvailability(value);
                          return isAvailable || "This email is already registered";
                        }
                      }
                    })}
                    error={errors.email}
                    success={!!(formData.email && validateEmail(formData.email) && !errors.email)}
                    placeholder="you@example.com"
                  />

                  <FormInput
                    label="Phone Number *"
                    icon={Phone}
                    type="tel"
                    isPhone={true}
                    register={register("phone_number", {
                      required: "Phone number is required",
                      pattern: {
                        value: /^\(\d{3}\) \d{3}-\d{4}$/,
                        message: "Please enter a valid phone number"
                      }
                    })}
                    onChange={handlePhoneChange('phone_number')}
                    error={errors.phone_number}
                    success={!!(formData.phone_number && /^\(\d{3}\) \d{3}-\d{4}$/.test(formData.phone_number) && !errors.phone_number)}
                    placeholder="(555) 123-4567"
                    maxLength={14}
                    className="md:col-span-2"
                  />

                  <div className="md:col-span-2">
                    <PasswordInput
                      label="Password *"
                      register={register("password", {
                        required: "Password is required",
                        validate: (value) => {
                          const strength = validatePassword(value);
                          return strength.isValid || "Password must meet all requirements";
                        }
                      })}
                      error={errors.password}
                      showPassword={showPassword}
                      onToggleShow={() => setShowPassword(!showPassword)}
                      placeholder="Create a strong password"
                    />
                    <AnimatePresence>
                      <PasswordStrength password={formData.password} />
                    </AnimatePresence>
                  </div>

                  <div className="md:col-span-2">
                    <PasswordInput
                      label="Confirm Password *"
                      register={register("confirmPassword", {
                        required: "Please confirm your password",
                        validate: (value) => value === formData.password || "Passwords do not match"
                      })}
                      error={errors.confirmPassword}
                      showPassword={showConfirmPassword}
                      onToggleShow={() => setShowConfirmPassword(!showConfirmPassword)}
                      placeholder="Confirm your password"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-6">
                  <motion.button
                    type="button"
                    disabled={!stepValidation.account}
                    onClick={() => goToStep('practice')}
                    whileHover={{ scale: stepValidation.account ? 1.02 : 1 }}
                    whileTap={{ scale: stepValidation.account ? 0.98 : 1 }}
                    className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${stepValidation.account
                        ? "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </motion.button>
                </div>
              </motion.div>
            )}

            {/* Practice Step */}
            {currentStep === 'practice' && (
              <motion.div
                key="practice"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormInput
                    label="Practice Name *"
                    icon={Building2}
                    register={register("practice_name", { required: "Practice name is required" })}
                    error={errors.practice_name}
                    placeholder="Enter your practice name"
                    className="md:col-span-2"
                  />

                  <FormInput
                    label="DBA (Doing Business As)"
                    register={register("practice_dba")}
                    placeholder="Optional"
                  />

                  <FormInput
                    label="EIN *"
                    register={register("practice_ein", { required: "EIN is required" })}
                    error={errors.practice_ein}
                    placeholder="XX-XXXXXXX"
                  />

                  <FormInput
                    label="Website"
                    icon={Globe}
                    type="url"
                    register={register("practice_website")}
                    placeholder="https://yourpractice.com"
                  />

                  <div className="hidden">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      NPI Type *
                    </label>
                    <select
                      {...register("practice_npi_type", {
                        required: "Please select an NPI Type"
                      })}
                      onChange={handleNpiTypeChange}
                      className={`w-full px-4 py-3 rounded-xl border-2 ${
                        errors.practice_npi_type
                          ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
                          : "border-gray-200 focus:border-indigo-500 focus:ring-indigo-500/20 hover:border-gray-300"
                      } focus:outline-none focus:ring-4 transition-all duration-200`}
                    >
                      <option value="NA">Not Available</option>
                      <option value="Individual">Individual</option>
                      <option value="Organization">Organization</option>
                    </select>
                    {errors.practice_npi_type && (
                      <p className="mt-1 text-sm text-red-600 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.practice_npi_type.message}
                      </p>
                    )}
                  </div>

                  {(formData.practice_npi_type === "Individual" || formData.practice_npi_type === "Organization") && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}>
                      <FormInput
                        label="Practice NPI Number *"
                        icon={FileText}
                        register={register("practice_npi_number")}
                        placeholder="10-digit NPI"
                        maxLength={10}
                        className="md:col-span-2"
                      />
                    </motion.div>
                  )}
                </div>

                <div className="flex justify-between pt-6">
                  <motion.button
                    type="button"
                    onClick={() => setCurrentStep('account')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="flex items-center px-6 py-3 text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </motion.button>
                  <motion.button
                    type="button"
                    disabled={!stepValidation.practice}
                    onClick={() => goToStep('confirmation')}
                    whileHover={{ scale: stepValidation.practice ? 1.02 : 1 }}
                    whileTap={{ scale: stepValidation.practice ? 0.98 : 1 }}
                    className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${stepValidation.practice
                        ? "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </motion.button>
                </div>
              </motion.div>
            )}


            {/* Confirmation Step */}
            {currentStep === 'confirmation' && (
              <motion.div
                key="confirmation"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="space-y-6"
              >
                <div className="bg-gradient-to-r from-emerald-50 to-blue-50 rounded-xl p-6 border border-emerald-200">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 mr-2 text-emerald-600" />
                    Review Your Information
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <User className="w-5 h-5 mr-2 text-indigo-600" />
                        Account Details
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Name</p>
                          <p className="text-base text-gray-900">{formData.given_name} {formData.family_name}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Email</p>
                          <p className="text-base text-gray-900">{formData.email}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Phone</p>
                          <p className="text-base text-gray-900">{formData.phone_number}</p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                        <Building2 className="w-5 h-5 mr-2 text-purple-600" />
                        Practice Information
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm font-medium text-gray-500">Practice Name</p>
                          <p className="text-base text-gray-900">{formData.practice_name}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">EIN</p>
                          <p className="text-base text-gray-900">{formData.practice_ein}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Email</p>
                          <p className="text-base text-gray-900">{formData.email}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-500">Phone</p>
                          <p className="text-base text-gray-900">{formData.phone_number}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h4 className="flex items-center text-sm font-medium text-gray-700 mb-3">🔒 Privacy &amp; Security</h4>
                  <p className="text-sm text-gray-600 mb-4">By completing registration, you confirm that all information is accurate and agree to our <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline">Privacy Policy</a>. Your data is protected with enterprise-grade security.</p>
                  <div className="space-y-3">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        {...register("termsAccepted", {
                          required: "You must accept the Terms of Service and Privacy Policy to continue"
                        })}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                      />
                      <span className="text-sm text-gray-700">I agree to the Terms of Service and Privacy Policy *</span>
                    </label>
                    {errors.termsAccepted && (
                      <p className="text-sm text-red-600 flex items-center ml-6">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        {errors.termsAccepted.message}
                      </p>
                    )}
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input type="checkbox" className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5" />
                      <span className="text-sm text-gray-700">I would like to receive updates about new features and services (optional)</span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-between pt-6">
                  <motion.button
                    type="button"
                    onClick={() => setCurrentStep('practice')}
                    disabled={isSubmitting}
                    whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                    whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
                    className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${isSubmitting
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed border-2 border-gray-200"
                        : "text-gray-700 bg-white border-2 border-gray-300 hover:bg-gray-50 hover:border-gray-400"
                      }`}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </motion.button>

                  <motion.button
                    type="button"
                    onClick={handleRegistration}
                    disabled={isSubmitting || registrationComplete || !formData.termsAccepted}
                    whileHover={{ scale: (isSubmitting || registrationComplete || !formData.termsAccepted) ? 1 : 1.02 }}
                    whileTap={{ scale: (isSubmitting || registrationComplete || !formData.termsAccepted) ? 1 : 0.98 }}
                    className={`flex items-center px-8 py-3 rounded-xl font-semibold transition-all duration-200 ${isSubmitting
                        ? "bg-blue-500 text-white cursor-not-allowed"
                        : registrationComplete
                          ? "bg-green-500 text-white cursor-not-allowed"
                          : !formData.termsAccepted
                            ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                            : "bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white shadow-lg"
                      }`}
                  >
                    {submitButtonContent}
                  </motion.button>
                </div>

                {/* Enhanced Error/Status Display */}
                <AnimatePresence>
                  {submitError && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, height: 0 }}
                      animate={{ opacity: 1, y: 0, height: 'auto' }}
                      exit={{ opacity: 0, y: -10, height: 0 }}
                      className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl relative"
                    >
                      <div className="flex items-start">
                        <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="font-medium">Registration Error</p>
                          <p className="text-sm mt-1">{submitError}</p>
                          <div className="flex items-center gap-3 mt-2">
                            {submitError.includes('already exists') ? (
                              <>
                                <button
                                  type="button"
                                  onClick={() => navigate('/login')}
                                  className="text-sm text-red-600 hover:text-red-800 font-medium underline"
                                >
                                  Go to Login
                                </button>
                                <span className="text-gray-400">|</span>
                              </>
                            ) : null}
                            <button
                              type="button"
                              onClick={() => {
                                setSubmitError(null);
                                setRegistrationStep('idle');
                                setFormDataForSubmission(null);
                                setIsSubmitting(false);
                                setRegistrationComplete(false);
                              }}
                              className="text-sm text-red-600 hover:text-red-800 underline"
                            >
                              Try Again
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default RegistrationPage;
